/**
 * Step 8 — AI demo — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 2 step 8
 *
 * Mason lock 2026-05-14: live Claude call (not pre-generated). User picks a
 * pre-curated chip OR types their own first message → real /chat function
 * streams Grace's response. Hard 10s timeout falls back to a canned response
 * if the API hiccups. One message per onboarding session.
 *
 * Phase A scope:
 *   - Default voice: Grace (tab='general'). Voice picker deferred until the
 *     server-side Premium gate on Logos/Spurgeon/Ezra is lifted for
 *     onboarding (Phase B).
 *   - Reuses /chat function — system prompt is server-baked per persona,
 *     so Grace's training carries over automatically.
 *   - Conversation persists to DB (no dry-run flag in /chat). Gets picked
 *     up at step 18 handoff to /chat (Phase B wiring).
 *
 * Wire shape mapped from subagent research of supabase/functions/chat/index.ts:
 *   - SSE stream, line-prefix `data: `
 *   - Event types: text (delta), conversation_id, assistant_message_id,
 *     stop_reason, meta, done, error
 *   - skipAutoRoute: true → lock the user's voice choice
 *   - skipTitleGen: true → skip background title-gen call
 */

import { useEffect, useRef, useState } from 'react'
import ContinueButton from '@/components/onboarding/ContinueButton'
import { sendChatMessage } from '@/lib/queries/chat'
import { getStartersFor } from '@/data/onboardingDemoStarters'
import { pickTopStruggle } from '@/data/onboardingPromptMatrix'
// Chat v2 atoms — Grace's avatar (☉ glyph + radial glow) + the lightweight
// streaming markdown renderer + blinking caret. Mirroring these in the
// onboarding demo means step 8 LOOKS like the real /chat surface, so the
// transition at step 18 handoff is visually seamless.
import { MODELS } from '@/components/chat/v2/data'
import { ModelGlyph, StreamCaret } from '@/components/chat/v2/atoms'
import StreamingContent from '@/components/chat/v2/StreamingContent'
// parseFollowUps strips the [FOLLOW_UPS]…[/FOLLOW_UPS] block from Grace's
// reply (and hides it mid-stream so the markers never flash). Server-side
// oneShotMode is supposed to suppress the block, but Grace's training is
// strong enough she sometimes appends it anyway. Belt-and-suspenders.
import { parseFollowUps } from '@/components/chat/v2/useVoiceFanOut'

const GRACE = MODELS.grace

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'
const TIMEOUT_MS = 10_000
const MAX_INPUT_CHARS = 500

// Canned fallback response when /chat times out or errors. Keeps the funnel
// flowing even if the API hiccups. Pastoral + Scripture-grounded so it still
// matches the demo promise.
const FALLBACK_RESPONSE =
  'I’m so glad you asked. I want to give you a real answer, but the connection is being slow on my end — let me leave you with this:\n\n' +
  '“Cast all your cares upon Him, for He cares for you.” — 1 Peter 5:7\n\n' +
  'Try this today: write down ONE thing you’ve been carrying, and pray it back to Him out loud — even if it sounds clunky. He’s not grading the prayer.'

// Strip trailing follow-up questions from Grace's response. The server-side
// `oneShotMode` directive tells Claude not to ask questions, but the model's
// pastoral training is so strong it slips through anyway. Belt-and-suspenders:
// after the stream completes, walk backwards from the end and drop sentences
// that end in "?" until we hit a declarative one. Preserves quotes, blockquotes,
// and any in-paragraph question marks (e.g., a quoted Scripture question).
//
// Conservative: only strips up to 2 trailing question sentences so we don't
// nuke the whole reply if Grace went rogue.
function stripTrailingQuestions(text) {
  if (!text) return text
  let out = text.trimEnd()
  for (let i = 0; i < 2; i++) {
    // Match the final sentence — handles ., ?, ! as terminators. Walks back
    // from the end through the last sentence boundary.
    const m = out.match(/([.!?])\s+([^.!?]+\?)\s*[”"']?\s*$/)
    if (!m) break
    if (!m[2].trim().endsWith('?')) break
    // Found a trailing "? sentence" — strip it.
    out = out.slice(0, m.index + 1).trimEnd()
  }
  // Edge case: if the WHOLE response was a question (single sentence ending
  // in ?), don't strip — leave it so user sees something.
  if (!out.trim()) return text
  return out
}

// Three-dot typing indicator while we wait for the first text delta.
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1.5" aria-label="Grace is typing">
      <span className="ai-demo-dot" />
      <span className="ai-demo-dot" style={{ animationDelay: '160ms' }} />
      <span className="ai-demo-dot" style={{ animationDelay: '320ms' }} />
    </span>
  )
}

export default function AiDemoSynthesis({ profile = {}, onContinue, onConversationCreated }) {
  // Resolve which 3 chips to surface based on goal × top struggle.
  const goal = profile.primary_goal || 'grow_closer'
  const struggles = Array.isArray(profile.current_struggles) ? profile.current_struggles : []
  const topStruggle = pickTopStruggle(struggles) || 'staying_consistent'
  const chips = getStartersFor(goal, topStruggle)

  const [mounted, setMounted] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [userMessage, setUserMessage] = useState(null)
  const [aiResponse, setAiResponse] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamComplete, setStreamComplete] = useState(false)
  const [usedFallback, setUsedFallback] = useState(false)

  const abortRef = useRef(null)
  const responseScrollRef = useRef(null)

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // Cancel any in-flight stream on unmount so we don't update state on an
  // unmounted component if the user navigates away mid-response.
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort()
    }
  }, [])

  // Auto-scroll the response container as text streams in.
  useEffect(() => {
    if (responseScrollRef.current) {
      responseScrollRef.current.scrollTop = responseScrollRef.current.scrollHeight
    }
  }, [aiResponse])

  async function sendMessage(text) {
    const trimmed = text.trim()
    if (!trimmed || isStreaming || userMessage) return

    setUserMessage(trimmed)
    setIsStreaming(true)
    setAiResponse('')

    const controller = new AbortController()
    abortRef.current = controller
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)

    try {
      const res = await sendChatMessage(
        {
          message: trimmed,
          tab: 'general', // Grace
          conversationId: null,
          attachments: [],
          skipAutoRoute: true,
          skipTitleGen: true,
          // Onboarding demo only — server appends a system suffix telling
          // Claude to give ONE complete pastoral response without back-and-
          // forth questions or the [FOLLOW_UPS] block. (Mason 2026-05-15:
          // first impression should be a finished thought, not a chat thread.)
          oneShotMode: true,
        },
        { signal: controller.signal }
      )

      // SSE parse loop. Each line: `data: <json>\n\n`. We only care about
      // `text` deltas and the terminal `done`/`error` events for the demo.
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let accumulated = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()
          if (!payload) continue
          let event
          try {
            event = JSON.parse(payload)
          } catch {
            continue
          }
          if (event.type === 'text' && typeof event.text === 'string') {
            accumulated += event.text
            // Strip [FOLLOW_UPS] block (and partial mid-stream openers) so
            // markers never flash in the bubble. Same parser chat v2 uses.
            setAiResponse(parseFollowUps(accumulated).cleanText)
          } else if (event.type === 'conversation_id' && event.id) {
            // /chat fires this once per stream, right after creating the
            // conversation row. Field is `id` (not conversationId — confirmed
            // at supabase/functions/chat/index.ts:1400). Surface it to the
            // parent so step 18 handoff can deep-link `/chat?conversation=<id>`
            // and re-open the demo exactly where the user left it.
            // (Mason 2026-05-15.)
            onConversationCreated?.(event.id)
          } else if (event.type === 'error') {
            throw new Error(event.error || 'Stream error')
          } else if (event.type === 'done') {
            // terminal — exit when the reader drains
          }
        }
      }

      clearTimeout(timeoutId)
      // Two-pass cleanup once the stream finishes:
      //   1. parseFollowUps — strips the [FOLLOW_UPS]…[/FOLLOW_UPS] block.
      //   2. stripTrailingQuestions — drops up to 2 trailing "?" sentences
      //      that slip through despite oneShotMode. (Grace's pastoral
      //      conversational training is strong; the server suffix is partial
      //      compliance at best.)
      // Edge case: stream ended cleanly but we never got any text. Treat as
      // failure so the user isn't staring at an empty bubble.
      const cleanedLive = parseFollowUps(accumulated).cleanText
      if (!cleanedLive.trim()) {
        setAiResponse(FALLBACK_RESPONSE)
        setUsedFallback(true)
      } else {
        const cleaned = stripTrailingQuestions(cleanedLive)
        setAiResponse(cleaned)
      }
      setIsStreaming(false)
      setStreamComplete(true)
    } catch (err) {
      clearTimeout(timeoutId)
      // AbortError = our timeout fired. Other errors = anything from the
      // server (rate limit, premium gate, network). Either way: serve the
      // canned fallback so the funnel doesn't break on the user.
      if (import.meta.env.DEV) {
        console.warn('[AI demo] falling back:', err?.message || err)
      }
      setAiResponse(FALLBACK_RESPONSE)
      setUsedFallback(true)
      setIsStreaming(false)
      setStreamComplete(true)
    }
  }

  function handleChipClick(chipText) {
    setInputValue(chipText)
    sendMessage(chipText)
  }

  function handleKeyDown(e) {
    // Enter sends, Shift+Enter inserts newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(inputValue)
    }
  }

  const sendDisabled = isStreaming || userMessage || !inputValue.trim()
  const showComposer = !userMessage

  return (
    <div className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-5 py-10 sm:py-12">
      <style>{`
        @keyframes aiDemoDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-3px); opacity: 1; }
        }
        .ai-demo-dot {
          display: inline-block;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--gold);
          animation: aiDemoDot 1100ms ease-in-out infinite;
        }
        @keyframes aiDemoFadeUp {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .ai-demo-chip {
          transition: border-color 200ms ${EASE}, background 200ms ${EASE},
                      transform 200ms ${EASE}, box-shadow 220ms ${EASE};
        }
        .ai-demo-chip:hover:not(:disabled) {
          border-color: var(--gold);
          background: var(--bg-surface-high);
          transform: translateY(-1px);
          box-shadow: 0 0 18px -8px rgba(212, 168, 67, 0.4);
        }
        .ai-demo-chip:disabled { opacity: 0.5; cursor: not-allowed; }
        .ai-demo-textarea {
          transition: border-color 200ms ${EASE}, background 200ms ${EASE};
        }
        .ai-demo-textarea:focus {
          outline: none;
          border-color: var(--gold);
          background: var(--bg-surface-high);
        }
        .ai-demo-send {
          transition: background 200ms ${EASE}, color 200ms ${EASE},
                      transform 200ms ${EASE};
        }
        .ai-demo-send:not(:disabled):hover {
          background: var(--gold-dim);
          transform: translateY(-1px);
        }
        .ai-demo-send:disabled { opacity: 0.4; cursor: not-allowed; }
        @media (prefers-reduced-motion: reduce) {
          .ai-demo-dot, .ai-demo-chip, .ai-demo-textarea, .ai-demo-send {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      {/* Lead */}
      <p
        className="text-[10px] uppercase tracking-[0.32em] text-[var(--gold)] text-center mb-3 font-semibold"
        style={{
          opacity: mounted ? 1 : 0,
          transition: `opacity 600ms ${EASE}`,
          fontFamily: 'var(--font-body)',
        }}
      >
        Try it now
      </p>

      {/* Heading */}
      <h1
        className="font-heading text-[26px] sm:text-[30px] text-center text-[var(--text-primary)] mb-7 leading-[1.18] tracking-[-0.005em]"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(6px)',
          transition: `opacity 700ms ${EASE} 80ms, transform 700ms ${EASE} 80ms`,
        }}
      >
        Ask Grace something.
      </h1>
      {/* Subheading "Pick a starter or type your own. She'll answer with
          Scripture." retired 2026-05-17 (Mason lock) — the starter chips +
          composer are self-explanatory, the extra sentence was noise. */}

      {/* COMPOSER — chips + input. Hidden once the user has sent their message. */}
      {showComposer && (
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(8px)',
            transition: `opacity 700ms ${EASE} 240ms, transform 700ms ${EASE} 240ms`,
          }}
        >
          {/* Chips */}
          <div className="flex flex-col gap-2.5 mb-5">
            {chips.map((chip, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleChipClick(chip)}
                disabled={isStreaming}
                className="ai-demo-chip text-left rounded-xl px-4 py-3 text-[14.5px] leading-[1.45]"
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--glass-border)',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                {chip}
              </button>
            ))}
          </div>

          {/* Free-text input + Send */}
          <div className="flex items-end gap-2">
            <textarea
              value={inputValue}
              onChange={e => setInputValue(e.target.value.slice(0, MAX_INPUT_CHARS))}
              onKeyDown={handleKeyDown}
              placeholder="Or type your own question…"
              disabled={isStreaming}
              rows={2}
              className="ai-demo-textarea flex-1 rounded-xl px-4 py-3 text-[14.5px] resize-none"
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--glass-border)',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-body)',
                lineHeight: 1.45,
                maxHeight: 120,
              }}
            />
            <button
              type="button"
              onClick={() => sendMessage(inputValue)}
              disabled={sendDisabled}
              className="ai-demo-send shrink-0 rounded-xl px-5 py-3 text-[14px] font-semibold"
              style={{
                background: sendDisabled ? 'var(--bg-surface)' : 'var(--gold)',
                color: sendDisabled ? 'var(--text-muted-soft)' : 'var(--text-on-gold)',
                border: '1px solid',
                borderColor: sendDisabled ? 'var(--glass-border)' : 'var(--gold)',
                fontFamily: 'var(--font-body)',
              }}
            >
              Send
            </button>
          </div>

          {/* Char counter — only shows when user has typed something */}
          {inputValue.length > 0 && (
            <p className="text-right text-[11px] text-[var(--text-muted-soft)] mt-1.5">
              {inputValue.length} / {MAX_INPUT_CHARS}
            </p>
          )}
        </div>
      )}

      {/* CHAT — user bubble + Grace bubble. Mirrors chat v2 patterns
          (DesktopTurnBlock + GuideColumn) so the visual carries over to
          /chat at step 18 handoff with zero discontinuity. */}
      {userMessage && (
        <div className="space-y-5" aria-live="polite">
          {/* User bubble — gold background, asymmetric top-right corner clip,
              gold glow shadow. Matches chat v2 desktop + mobile turn blocks. */}
          <div
            className="flex justify-end"
            style={{ animation: `aiDemoFadeUp 400ms ${EASE} both` }}
          >
            <div
              className="ph-mask max-w-[85%] whitespace-pre-wrap break-words"
              style={{
                padding: '10px 18px',
                background: 'var(--gold)',
                color: 'var(--text-on-gold)',
                borderRadius: 18,
                borderTopRightRadius: 6,
                fontFamily: 'var(--font-body)',
                fontSize: 13.5,
                fontWeight: 500,
                lineHeight: 1.5,
                textAlign: 'left',
                boxShadow: '0 0 14px rgba(233, 195, 73, 0.18)',
              }}
            >
              {userMessage}
            </div>
          </div>

          {/* Grace header — ModelGlyph avatar (☉ glyph + radial glow) +
              name in serif gold + GENERAL eyebrow. Identical to GuideColumn. */}
          <div
            className="flex items-center gap-2.5"
            style={{ animation: `aiDemoFadeUp 400ms ${EASE} 100ms both` }}
          >
            <ModelGlyph model={GRACE} size={32} />
            <div className="min-w-0">
              <div
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  fontSize: 15,
                  color: GRACE.accent,
                  lineHeight: 1.1,
                }}
              >
                {GRACE.name}
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-label, var(--font-body))',
                  fontSize: 9.5,
                  fontWeight: 600,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                {GRACE.eyebrow}
              </div>
            </div>
          </div>

          {/* Grace response bubble — chat v2 GuideColumn styling: dark navy
              translucent + gold-tinted border + asymmetric top-left corner
              clip + backdrop blur. StreamingContent renders the lightweight
              markdown (bold, italic, headers, blockquotes) live as text
              streams in. StreamCaret blinks at the tail while streaming. */}
          <div
            ref={responseScrollRef}
            className="ph-mask dark-scrollbar"
            style={{
              padding: '16px 18px',
              background: 'rgba(20, 26, 50, 0.7)',
              border: `1px solid ${GRACE.accent}33`,
              borderRadius: 16,
              borderTopLeftRadius: 6,
              fontFamily: 'var(--font-body)',
              fontSize: 14,
              lineHeight: 1.65,
              color: 'var(--text-primary)',
              backdropFilter: 'blur(8px)',
              maxHeight: 360,
              overflowY: 'auto',
              minHeight: 64,
              animation: `aiDemoFadeUp 400ms ${EASE} 180ms both`,
            }}
          >
            {aiResponse ? (
              <>
                <StreamingContent content={aiResponse} />
                {isStreaming && <StreamCaret color={GRACE.accent} height={16} width={6} />}
              </>
            ) : isStreaming ? (
              <TypingDots />
            ) : null}
          </div>

          {usedFallback && (
            <p
              className="text-[11px] text-[var(--text-muted-soft)] -mt-2"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Slow connection — Grace had to grab a backup answer. Real conversations in chat will
              come through normally.
            </p>
          )}
        </div>
      )}

      {/* CTA — only shows once response is complete (or fallback fired). */}
      {streamComplete && (
        <div
          className="mt-9 flex justify-center"
          style={{ animation: `aiDemoFadeUp 500ms ${EASE} both` }}
        >
          <ContinueButton onClick={onContinue}>Continue</ContinueButton>
        </div>
      )}
    </div>
  )
}
